// Types
import { MapElement } from "@/components/ReportLayout/widgets/MapView/utils/types";

// Move UploadId type here to break circular dependency
export type UploadId = number | null;

// Move GISType enum here to break circular dependency
export enum GISType {
  DMA = "DMA",
  PIPES = "PIPES",
  ASSETS = "ASSETS"
}

export enum STANDARDISING {
  MATERIAL = "material",
  STATUS = "status",
  TYPE = "type", // type can be used for pipe_type or asset_type
  UNITS = "diameter_units"
}
export interface PostUploadGISFilesParams {
  company_id: number;
}

export interface PostUploadGISDataParams {
  company_id: number;
  upload_id: number;
}

export enum GIS_COLUMN_MAPPING {
  ASSET_TYPE = "asset_type",
  MATERIAL = "material",
  PIPE_TYPE = "pipe_type",
  STATUS = "status",
  UNITS = "units"
}

export type MappedColumns = {
  columns: {
    id: number;
    title: string;
  }[];
  material: Item[] | [];
  asset_type: Item[] | [];
  pipe_type: Item[] | [];
  status: Item[] | [];
  units: number | Item[] | [];
};

type MappedCreatedColumn = {
  mapped_column_id: number;
  column_name: CreatedColumnName;
  column_value: string;
};

export interface ColumnMappingBody {
  upload_id: number | null;
  filename: string;
  mapped_columns: MappedColumns;
  new_column_data: MappedCreatedColumn[];
}

export type UnselectedColumns = {
  required: Column[];
  optional: Column[];
};

export interface Column {
  column_name: ColumnName;
  column_type?: ColumnDataType;
  id: number;
  required: boolean;
}
export interface TableColumns {
  table_name: string;
  columns: Column[];
}

export type ColumnRequired =
  "key" | "diameter" | "material" | "type" | "status";

export type ColumnOptional =
  "name" | "diameter_units" | "subtype" | "created_date" | "dma_key";

export type ColumnName = ColumnRequired | ColumnOptional;

export type CreatedColumnName =
  | "dma_key"
  | "type"
  | "asset_key"
  | "asset_type"
  | "pipe_type"
  | "material"
  | "diameter";

export type ColumnDataType = "str" | "int" | "float" | "datetime" | "date";

export interface SampleData {
  file_name: string;
  type: string;
  data: Record<string, string | number>[];
  column_info: Record<string, ColumnDataType>;
}

export type ColumnMapping = {
  filename: string;
  mapped_columns: MappedColumns;
};

export interface ColumnAndSampleDataResponse {
  column_mappings: ColumnMapping[];
  table_columns: TableColumns[];
  sample_data: SampleData[];
}

interface AssetType {
  id: number;
  standardised_type: string;
}

interface PipeType {
  id: number;
  standardised_type: string;
  type: string | null;
}
interface PipeMaterial {
  id: number;
  material: string | null;
  standardised_material: string;
}

interface Units {
  id: number;
  standardised_unit: string;
  unit: string;
}

interface PipeStatus {
  id: number;
  standardised_status: string;
  status: string;
}

export interface UniqueValuesResponse {
  unique_values: string[];
  asset_type: AssetType[] | null;
  pipe_material: PipeMaterial[] | null;
  pipe_type: PipeType[] | null;
  pipe_status: PipeStatus[] | null;
  diameter_units: Units[] | null;
}

export interface DiscardFileBody {
  upload_id: UploadId;
  company_id: number | null;
  filename: string;
}

interface Preview {
  data: MapElement[];
}

export type SelectedValue = {
  value: string;
  title: string;
  name: string;
  type?: GIS_COLUMN_MAPPING;
  required?: boolean;
};

export type Item = {
  title: string;
  type: string;
};

export type FileSelectedValues = {
  file: string;
  material?: Item[];
  asset_type?: Item[];
  pipe_type?: Item[];
  status?: Item[];
  units?: Item[] | number;
  data: SelectedValue[];
};

export enum ErrorModalText {
  DEFAULT_ERROR_MESSAGE = 1,
  COLUMNS_NOT_SELECTED = 2,
  SYSTEM_FIELDS_NOT_MAPPED = 3,
  SYSTEM_FIELD_NUMERIC = 4
}

export enum GISRoutes {
  UPLOAD_SHAPEFILE = 0,
  PROCESS_DMA = 1,
  PROCESS_PIPES = 2,
  PROCESS_ASSETS = 3,
  MAP_PREVIEW = 4
}

export type MappedSystemFields = {
  material: boolean;
  asset_type: boolean;
  pipe_type: boolean;
  status: boolean;
  units: boolean;
};

export type CreatedColumn = {
  id: string;
  mappedColumnId: number;
  name: ColumnName;
  value: string;
  units?: "1" | "2"; // 1 = inch, 2 = mm
};

export type GISUploadData = {
  companyId: number | null;
  selectedValues: FileSelectedValues[];
  createdColumns: CreatedColumn[];
  fileIndex: number;
  currentStep: GISRoutes;
  mappedSystemFields: MappedSystemFields;
  returnToProject: string | null;
  returnToCompany: string | null;
};

export type GISUploadValues = {
  isLoading: boolean;
  // isUploading: boolean; // isLoading
  uploadStatus: "uninitialized" | "uploading" | "success" | "error";
  isErrorModalOpen: boolean; // isErrorModalVisible
  errors: Array<ErrorModalText>;
  data: GISUploadData;
};

export type GISUploadState = {
  gisUpload: {
    [key: number]: GISUploadValues;
  };
};

export type PreviewValuesResponse = {
  map: {
    assets: Preview;
    dmas: Preview;
    pipes: Preview;
  };
  odl_task_id?: string;
};

export type GISMapPreviewProps = {
  companyId: number | null;
  uploadId: UploadId;
  withCache?: boolean;
};
